import React, { useCallback, useMemo } from 'react';
import styles from './OptionsList.module.scss';
import TrashIcon from '../../../../../../../../assets/icons/trash.svg?react';
import { TextInput, TextInputValue } from '../../../../../../../../components/text-input/TextInput';
import { SelectOption, FeedbackFormElementMultiScaleStatement } from '../../../../../../../../types';
import Select from '../../../../../../../../components/select/Select';
import { ValidationErrors } from '../../useFeedbackFormEdit';
import CircleCheckbox from '../../../../../../../../components/checkbox/CircleCheckbox';
import IconPlus from '../../../../../../../../assets/icons/plus_circle_filled.svg?react';
import ArrowLink from '../../../../../../../../assets/icons/arrow_link.svg?react';

type Props = {
  options: string[] | FeedbackFormElementMultiScaleStatement[];
  showError: boolean;
  onRemove: (index: number) => void;
  onChange: (index: number, value: TextInputValue) => void;
  onChangeAnswerOption?: (index: number, value: string) => void;
  onAdd: () => void;
  parentIndex: number;
  isAssessable?: boolean;
  isMultiScale?: boolean;
  answerOptions?: string[];
  checkedList?: boolean[];
  onChangeChecks?: (checkedList: boolean[], parentIndex: number) => void;
  isMultiChoiceAssessable?: boolean;
};

function OptionsList(props: Props) {
  const { options, showError, onRemove, onChange, onAdd, parentIndex, isAssessable, isMultiScale, checkedList, answerOptions, onChangeChecks, onChangeAnswerOption, isMultiChoiceAssessable } = props;
  const handleRemoveOption = (index: number) => () => {
    onRemove(index);
  };

  const handleChangeOption = (index: number) => (value: TextInputValue) => {
    onChange(index, value);
  };

  const handleChangeMultiScaleAssessment = (index: number) => (value: SelectOption) => {
    onChangeAnswerOption && onChangeAnswerOption(index, value.value);
  };

  const handleAddOption = () => onAdd();

  const optionsLength = options.length;

  const handleCheckChange = useCallback((index: number) => (state: boolean) => {
    if (onChangeChecks) {
      let newCheckedList;
      if (isMultiChoiceAssessable) {
        newCheckedList = [...(checkedList || [])];
        newCheckedList[index] = state;
      } else {
        newCheckedList = Array(options.length).fill(false);
        if (state) {
          newCheckedList[index] = true;
        }
      }
      onChangeChecks(newCheckedList, parentIndex);
    }
  }, [checkedList, isMultiChoiceAssessable, onChangeChecks, options.length, parentIndex]);

  const hasAnswerOptions = useMemo(() => answerOptions && answerOptions.length > 0, [answerOptions]);
  const answerSelectOptions = answerOptions && answerOptions.length > 0 ? answerOptions.map(answer => ({ label: answer, value: answer })) : [];

  return (
    <>
      {(options ?? []).map((o, index) => {
        const optionStr: string = isMultiScale ? (o as FeedbackFormElementMultiScaleStatement).value : o as string;
        const error = showError && !o;
        return (
          <div key={index} className={`${styles.row} ${styles.option}`}>
            <button className={styles.removeButton} onClick={handleRemoveOption(index)}>
              <TrashIcon stroke='#999999' />
            </button>
            <div className={`${styles.questionBox} ${isMultiScale && isAssessable ? styles.multiScaleQuestionBox : ''}`}>
              <TextInput
                label={''}
                id={`${parentIndex}-${index}`}
                value={optionStr}
                onChange={handleChangeOption(index)}
                hasError={error}
                subTitle={error ? ValidationErrors.empty : ''}
              />
              {isMultiScale && isAssessable && answerOptions && (
                <div className={styles.selectWrapper}>
                  <Select placeholder={hasAnswerOptions ? 'Select the correct answer' : 'Please input answers below first'}
                    disabled={!hasAnswerOptions}
                    options={answerSelectOptions}
                    onChange={handleChangeMultiScaleAssessment(index)}
                    value={(o as FeedbackFormElementMultiScaleStatement).assessment}
                    showSearch={false}
                    doNotSort
                  />
                  <ArrowLink className={styles.arrowLink} />
                </div>
              )}
            </div>
            {!!isAssessable && checkedList && (
              <div className={styles.checkBox}>
                <CircleCheckbox
                  id={`${parentIndex}-${index}-Correct`} checked={!!checkedList?.[index]}
                  onChange={handleCheckChange(index)}
                  isRoundRect={isMultiChoiceAssessable}
                />
              </div>
            )}
          </div>
        );
      })}
      <div className={styles.row}>
        <button className={styles.button} onClick={handleAddOption}>
          <IconPlus className={styles.iconPlus}/>
        </button>
        {showError && optionsLength <= 1 && <div className={styles.error}>Please, add at least 2 options</div>}
      </div>
    </>
  );
}

export default OptionsList;
